import React, { useMemo, useState } from "react";
import api from "../api/axios";

const STEPS = ["Identity", "Demographics", "Relation"];

export default function PatientWizard({ patient = null, onClose, onSaved }) {
  const isEdit = !!patient;

  const initialForm = useMemo(
    () => ({
      name: patient?.name || "",
      age: patient?.age ?? "",
      gender: patient?.gender || "male",
      relation: patient?.relation || "self",
      phone: patient?.phone || "",
      notes: patient?.notes || "",
    }),
    [patient]
  );

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const next = () => setStep((prev) => prev + 1);
  const prev = () => setStep((prev) => prev - 1);

  const canNext = () => {
    if (step === 0) return form.name.trim().length > 1;
    if (step === 1) return form.age !== "" && form.gender;
    return true;
  };

  const save = async () => {
    try {
      setSaving(true);

      const payload = {
        name: form.name.trim(),
        age: form.age === "" ? null : Number(form.age),
        gender: form.gender,
        relation: form.relation.trim() || "self",
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      };

      const res = isEdit
        ? await api.put(`/api/patient/${patient.id}`, payload)
        : await api.post("/api/patient/", payload);

      onSaved(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        alert(detail.map((item) => item.msg).join("\n"));
      } else {
        alert(detail || `Could not ${isEdit ? "update" : "create"} patient`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="wizard-modal">
        <div className="wizard-header">
          <div>
            <h2>{isEdit ? "Edit Patient" : "Add Patient"}</h2>
            <p className="muted">
              {isEdit
                ? "Update the patient profile details."
                : "Create a patient profile before opening a medical session."}
            </p>
          </div>

          <button className="btn btn-ghost" onClick={onClose} type="button">
            ✕
          </button>
        </div>

        <div className="wizard-steps">
          {STEPS.map((label, index) => (
            <div
              key={label}
              className={`wizard-step ${index === step ? "active" : ""} ${index < step ? "done" : ""}`}
            >
              <span>{index + 1}</span>
              <p>{label}</p>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="wizard-body">
            <div className="field-block">
              <label>Patient Name</label>
              <input
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Enter full name"
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="wizard-body two-col">
            <div className="field-block">
              <label>Age</label>
              <input
                value={form.age}
                onChange={(e) => updateField("age", e.target.value)}
                placeholder="Age"
                type="number"
                min="0"
              />
            </div>

            <div className="field-block">
              <label>Gender</label>
              <select
                value={form.gender}
                onChange={(e) => updateField("gender", e.target.value)}
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="wizard-body">
            <div className="field-block">
              <label>Relation</label>
              <input
                value={form.relation}
                onChange={(e) => updateField("relation", e.target.value)}
                placeholder="self / mother / father / spouse / child"
              />
            </div>

            <div className="field-block">
              <label>Phone</label>
              <input
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                placeholder="Optional phone"
              />
            </div>

            <div className="field-block">
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Optional notes"
                rows={4}
              />
            </div>
          </div>
        )}

        <div className="wizard-footer">
          <button className="btn btn-secondary" onClick={prev} disabled={step === 0} type="button">
            Back
          </button>

          {step < STEPS.length - 1 ? (
            <button className="btn btn-primary" onClick={next} disabled={!canNext()} type="button">
              Next
            </button>
          ) : (
            <button className="btn btn-primary" onClick={save} disabled={saving} type="button">
              {saving ? (isEdit ? "Updating..." : "Saving...") : isEdit ? "Update Patient" : "Create Patient"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}